import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const HeroContainer = styled.div`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #faf8f5 0%, #f5f0eb 100%);
`;

const NameContainer = styled.div`
  text-align: center;
  z-index: 10;
  position: relative;
`;

const MainName = styled.h1`
  font-size: clamp(3rem, 8vw, 8rem);
  font-weight: 300;
  color: #d4737d;
  margin: 0;
  font-family: 'Playfair Display', serif;
  letter-spacing: -0.02em;
  line-height: 0.9;
`;

const SubName = styled.h2`
  font-size: clamp(1.5rem, 4vw, 4rem);
  font-weight: 400;
  color: #8b6f7d;
  margin: 0.5rem 0 0 0;
  font-family: 'Playfair Display', serif;
  font-style: italic;
`;

const ScatteredImage = styled(motion.div)`
  position: absolute;
  width: 180px;
  height: 180px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 20px;
  }
  
  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
    /* give more horizontal breathing room on small screens */
    /* keep left/right percentages but reduce size; spacing adjustments handled in JS */
  }
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #8b6f7d;
  font-size: 0.9rem;
  
  &::after {
    content: '';
    width: 2px;
    height: 30px;
    background: #d4737d;
    margin-top: 0.5rem;
    animation: bounce 2s infinite;
  }
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-10px);
    }
    60% {
      transform: translateY(-5px);
    }
  }
`;

const Hero = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Define scattered positions for images around the name
  const imagePositions = [
    // Left side images
    { top: '15%', left: '25%', side: 'left' },
    { top: '35%', left: '20%', side: 'left' },
    { top: '55%', left: '28%', side: 'left' },
    { top: '70%', left: '22%', side: 'left' },
    // Right side images
    { top: '10%', right: '25%', side: 'right' },
    { top: '30%', right: '20%', side: 'right' },
    { top: '50%', right: '28%', side: 'right' },
    { top: '65%', right: '22%', side: 'right' },
  ];

  const getImageStyle = (position, index) => {
    // add extra horizontal offset on small screens so images aren't too close
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

    // base transform moves images left/right with scroll for a parallax feel
    const baseTransform = position.side === 'left'
      ? -scrollY * 0.8
      : scrollY * 0.8;

    // mobileOffset pushes left-side images further left and right-side images further right
    const mobileOffset = isMobile ? (position.side === 'left' ? -40 : 40) : 0;

    // Adjust left/right percentage slightly on mobile so images move further from center
    const finalPosition = { ...position };
    if (isMobile) {
      const deltaPercent = 8; // how many percentage points to push outward on mobile

      if (position.left) {
        const match = /^([0-9]+)\s*%$/.exec(position.left);
        if (match) {
          const num = Number(match[1]);
          const newVal = Math.max(2, num - deltaPercent);
          finalPosition.left = `${newVal}%`;
        }
      }

      if (position.right) {
        const match = /^([0-9]+)\s*%$/.exec(position.right);
        if (match) {
          const num = Number(match[1]);
          const newVal = Math.max(2, num - deltaPercent);
          finalPosition.right = `${newVal}%`;
        }
      }
    }

    return {
      transform: `translateX(${baseTransform + mobileOffset}px) rotate(${Math.sin(index) * 5}deg)`,
      opacity: Math.max(0, 1 - scrollY * 0.002),
      ...finalPosition
    };
  };

  const nameVariants = {
    opacity: Math.max(0, 1 - scrollY * 0.003),
    y: -scrollY * 0.2
  };

  return (
    <HeroContainer>
      {/* Scattered Images */}
      {imagePositions.map((position, index) => (
        <ScatteredImage
          key={`image-${index}`}
          style={getImageStyle(position, index)}
          transition={{ type: "spring", stiffness: 100, damping: 20 }}
        >
          <img 
            src={`/${index + 1}.png`} 
            alt={`Portfolio ${index + 1}`}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = `Image ${index + 1}`;
            }}
          />
        </ScatteredImage>
      ))}

      {/* Main Name */}
      <motion.div
        style={nameVariants}
        transition={{ type: "spring", stiffness: 100, damping: 20 }}
      >
        <NameContainer>
          <MainName>SAULĖ</MainName>
          <SubName>Skodminaitė</SubName>
        </NameContainer>
      </motion.div>

      <ScrollIndicator>
        Scroll to explore
      </ScrollIndicator>
    </HeroContainer>
  );
};

export default Hero;